// src/scenes/RunStartScene.ts
//
// Phase 16 (FICHE-10, FICHE-11, UI-16): Run-start fiche selection.
// Presents 3 random common fiches (excluding 'standard'); player picks one
// to join 2× standard in their starting deck. Deterministic per seed.

import Phaser from 'phaser';
import type { FicheDef } from '../state/types';
import { GameState, ficheFromDef } from '../state/GameState';
import { FICHES, ficheSpritesheet } from '../content/fiches';
import { pickFromArray } from '../systems/rng';
import { addCrispText } from '../ui/textHelper';
import { saveRun } from '../systems/save';

const CARD_WIDTH = 100;
const CARD_HEIGHT = 140;
const CARD_CENTERS_X = [80, 188, 296];
const CARD_TOP_Y = 100;
const CHIP_Y = 310; // chip row, centered below cards (card bottom = 240, gap 30px, chip 80×80)
const BTN_Y = 420;

export class RunStartScene extends Phaser.Scene {
  private gs!: GameState;
  private candidates: FicheDef[] = [];
  private selectedIdx: number | null = null;
  private cardRects: Phaser.GameObjects.Rectangle[] = [];
  private startBtn!: Phaser.GameObjects.Rectangle;
  private startBtnLabel!: Phaser.GameObjects.Text;
  private btnListenerActive = false;

  constructor() {
    super({ key: 'RunStart' });
  }

  init(data: { gameState: GameState }): void {
    this.gs = data.gameState;
    this.selectedIdx = null;
    this.candidates = [];
    this.cardRects = [];
    this.btnListenerActive = false;
  }

  create(): void {
    // Phase 17: navy background + bg-tile overlay
    this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x0d1b2a).setOrigin(0, 0);
    if (this.textures.exists('bg-tile')) {
      const tileW = 128;
      const tileH = 128;
      const cols = Math.ceil(this.scale.width / tileW) + 1;
      const rows = Math.ceil(this.scale.height / tileH) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          this.add
            .image(c * tileW, r * tileH, 'bg-tile')
            .setOrigin(0, 0)
            .setAlpha(0.08);
        }
      }
    }

    // Scene title
    addCrispText(this, 187, 52, 'Choose Your Starter', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#ffffff',
      fontSize: '20px',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // Draw 3 random common fiches (excluding 'standard') without replacement
    const rng = this.gs.getRng();
    const commonPool = FICHES.filter((f) => f.rarity === 'common' && f.defId !== 'standard');
    this.candidates = [];
    const pool: FicheDef[] = [...commonPool];
    for (let i = 0; i < 3 && pool.length > 0; i++) {
      const picked = pickFromArray(rng, pool);
      this.candidates.push(picked);
      pool.splice(pool.indexOf(picked), 1);
    }

    // Fallback: pad to 3 if common pool is smaller than 3
    while (this.candidates.length < 3) {
      this.candidates.push(commonPool[0] ?? (FICHES[0] as FicheDef));
    }

    // Draw the 3 cards
    this.cardRects = [];
    this.candidates.forEach((def, idx) => {
      this.drawCard(def, idx);
    });

    // Chip row — one per card, centered below card column, 80×80
    const chipColors: Record<string, number> = {
      common: 0xffffff,
      uncommon: 0x88aaff,
      rare: 0xcc44ff,
      legendary: 0xd4af37,
    };
    this.candidates.forEach((def, idx) => {
      const cx = CARD_CENTERS_X[idx] ?? 188;
      const sheet = ficheSpritesheet(def.defId);
      if (sheet && this.textures.exists(sheet.key)) {
        this.add.image(cx, CHIP_Y, sheet.key, sheet.frame).setDisplaySize(80, 80).setOrigin(0.5);
      } else {
        const ficheKey = `fiche-${def.defId}`;
        const chipKey = `chip-${def.rarity}`;
        const key = this.textures.exists(ficheKey)
          ? ficheKey
          : this.textures.exists(chipKey)
            ? chipKey
            : null;
        if (key) {
          this.add.image(cx, CHIP_Y, key).setDisplaySize(80, 80).setOrigin(0.5);
        } else {
          this.add.circle(cx, CHIP_Y, 40, chipColors[def.rarity] ?? 0xffffff, 0.4);
        }
      }
    });

    // "START RUN" button (inactive initially)
    this.startBtn = this.add.rectangle(187, BTN_Y, 240, 44, 0x333333).setOrigin(0.5);
    this.startBtnLabel = addCrispText(this, 187, BTN_Y, 'START RUN', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#888888',
      fontSize: '14px',
    }).setOrigin(0.5);
  }

  private rarityColor(rarity: string): string {
    switch (rarity) {
      case 'common':
        return '#ffffff';
      case 'uncommon':
        return '#88aaff';
      case 'rare':
        return '#cc44ff';
      case 'legendary':
        return '#d4af37';
      default:
        return '#ffffff';
    }
  }

  private drawCard(def: FicheDef, idx: number): void {
    const cx = CARD_CENTERS_X[idx] ?? 188;
    const cy = CARD_TOP_Y + CARD_HEIGHT / 2;
    const y = CARD_TOP_Y;

    // Card background rect (centered at cx, cy)
    const rect = this.add
      .rectangle(cx, cy, CARD_WIDTH, CARD_HEIGHT, 0x0a3d20)
      .setStrokeStyle(1, 0x444444);
    rect.setInteractive({ useHandCursor: true });
    rect.on('pointerdown', () => this.onCardTapped(idx));
    this.cardRects.push(rect);

    // Fiche name (14px bold, white)
    addCrispText(this, cx, y + 12, def.name, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#ffffff',
      fontSize: '14px',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // Rarity badge (11px, rarity color)
    addCrispText(this, cx, y + 28, def.rarity, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: this.rarityColor(def.rarity),
      fontSize: '11px',
    }).setOrigin(0.5, 0);

    // Chips/mult line (11px, muted)
    addCrispText(this, cx, y + 44, `+${def.chips} chips · ×${def.mult} mult`, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#aaaaaa',
      fontSize: '11px',
    }).setOrigin(0.5, 0);

    // Effect description (11px, word-wrapped at 88px)
    addCrispText(this, cx, y + 60, def.description ?? '', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#cccccc',
      fontSize: '11px',
      wordWrap: { width: 88 },
    }).setOrigin(0.5, 0);
  }

  private onCardTapped(idx: number): void {
    // Deselect previous card
    if (this.selectedIdx !== null) {
      this.cardRects[this.selectedIdx]?.setStrokeStyle(1, 0x444444);
    }

    if (this.selectedIdx === idx) {
      // Toggle off: tapping selected card again deselects it
      this.selectedIdx = null;
      this.setButtonActive(false);
    } else {
      // Select new card
      this.selectedIdx = idx;
      this.cardRects[idx]?.setStrokeStyle(3, 0xd4af37);
      this.setButtonActive(true);
    }
  }

  private setButtonActive(active: boolean): void {
    if (active) {
      this.startBtn.setFillStyle(0xd4af37);
      this.startBtnLabel.setStyle({ color: '#000000' });
      // Register the persistent handler once — it guards on selectedIdx internally
      if (!this.btnListenerActive) {
        this.startBtn.setInteractive({ useHandCursor: true });
        this.startBtn.on('pointerdown', () => {
          if (this.selectedIdx !== null) this.onConfirm();
        });
        this.btnListenerActive = true;
      }
    } else {
      this.startBtn.setFillStyle(0x333333);
      this.startBtnLabel.setStyle({ color: '#888888' });
      // Keep interactive (listener already guards via selectedIdx check)
      // so we don't stack listeners — just leave btn non-visually-active.
    }
  }

  private onConfirm(): void {
    if (this.selectedIdx === null) return;
    const chosenDef = this.candidates[this.selectedIdx];
    if (!chosenDef) return;
    const chosenFiche = ficheFromDef(chosenDef.defId);
    this.gs.addFicheToDeck(chosenFiche);
    saveRun(this.gs.toJSON());
    this.scene.start('Round', { gameState: this.gs });
  }
}
